import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import Nav from "@/components/site/Nav";
import Footer from "@/components/site/Footer";
import SectionBadge from "@/components/site/SectionBadge";
import BookConsultationDialog from "@/components/site/BookConsultationDialog";
import JoinUsDialog from "@/components/site/JoinUsDialog";
import FloatingActions from "@/components/site/FloatingActions";
import { track } from "@/lib/analytics";
import { usePageMeta } from "@/lib/usePageMeta";
import { INSIGHTS, readTimeLabel } from "@/data/insights";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const GRID = "max-w-7xl mx-auto px-6 sm:px-8 md:px-12";

/* Real counts verified against the sitemap (2026-10-21). */
const PRACTICES = [
  {
    name: "Strategy & Commercial Transformation",
    href: "/capabilities/strategy-commercial-transformation",
    count: "7 specialist services",
    desc: "Sales transformation, go-to-market, channel & route-to-market, pricing, product portfolio and commercial operating models.",
  },
  {
    name: "Market & Customer Intelligence",
    href: "/capabilities/market-customer-intelligence",
    count: "6 specialist services",
    desc: "Market sizing, buyer insights, segmentation, competitive intelligence, decision journeys and trends foresight.",
  },
  {
    name: "Brand, Marketing & Communications",
    href: "/capabilities/brand-marketing-communications",
    count: "6 specialist services",
    desc: "Brand strategy, positioning, marketing planning, integrated communications and marketing ROI.",
  },
  {
    name: "Digital & Commerce",
    href: "/capabilities/digital-commerce",
    count: "8 specialist services",
    desc: "E-commerce and marketplace growth, D2C performance marketing, unit economics and digital shelf excellence.",
  },
];

const SERVICES = [
  {
    name: "Sales Transformation",
    desc: "Improve sales productivity, territory coverage, sales process discipline, pipeline conversion and incentive structures.",
    href: "/capabilities/strategy-commercial-transformation/sales-transformation",
  },
  {
    name: "Marketing Transformation",
    desc: "Align marketing investment with the customer purchase journey, demand generation, brand positioning and measurable ROI.",
    href: "/capabilities/brand-marketing-communications/marketing-strategy-integrated-communications",
  },
  {
    name: "Go-to-Market Transformation",
    desc: "Design GTM strategies for new geographies, product categories, customer segments and digital/offline sales channels.",
    href: "/capabilities/strategy-commercial-transformation/go-to-market-market-entry",
  },
  {
    name: "Channel & Distribution",
    desc: "Strengthen distributor and dealer productivity, route-to-market design, channel economics, market coverage and partner performance.",
    href: "/capabilities/strategy-commercial-transformation/channel-route-to-market",
  },
  {
    name: "Pricing & Profitability",
    desc: "Find and plug pricing leakage, rebuild discount discipline and grow revenue while protecting margin.",
    href: "/capabilities/strategy-commercial-transformation/pricing-revenue-management",
  },
  {
    name: "Commercial Excellence",
    desc: "Build stronger commercial operating models, KPIs, governance, accountability and performance management systems.",
    href: "/capabilities/strategy-commercial-transformation/commercial-excellence-operating-model",
  },
];

const PROCESS = [
  { name: "Diagnose", desc: "Uncover the root causes of sales productivity loss, channel friction or marketing underperformance." },
  { name: "Strategise", desc: "Develop practical, commercially sound strategies aligned to customer and market realities." },
  { name: "Design", desc: "Build playbooks, incentive models, coverage plans and messaging architectures." },
  { name: "Execute", desc: "Work directly with commercial teams in the field to land implementation." },
  { name: "Measure", desc: "Track progress through clear KPIs, pipeline reviews and margin scorecards." },
  { name: "Institutionalise", desc: "Embed routines and capabilities so performance continues after the engagement ends." },
];

const AUDIENCE = [
  { name: "Promoter-Led & Family Businesses", desc: "Founder-led companies transitioning from organic momentum to professionalised, scalable commercial engines." },
  { name: "High-Growth Startups", desc: "Venture-backed or bootstrapped companies building predictable go-to-market engines and channel networks." },
  { name: "Mid-Market Enterprises", desc: "Established companies sharpening sales force productivity, pricing discipline and digital commerce expansion." },
  { name: "Large Corporations", desc: "Enterprises launching new categories, restructuring distributor networks or undergoing commercial transformation." },
];

const INSIGHT_SLUGS = [
  "when-to-hire-sales-transformation-consultant",
  "how-to-diagnose-sales-productivity",
  "why-distributor-network-underperforming",
  "why-growth-not-translating-into-profit",
];

const FAQS = [
  {
    q: "What is Sales & Marketing Transformation consulting?",
    a: "Sales & Marketing Transformation consulting evaluates and restructures a company's commercial operations — sales organisation, channel strategy, go-to-market execution, pricing and marketing return on investment — to turn revenue growth into predictable, profitable business performance.",
  },
  {
    q: "How does BLEZZINGS Growth Partners differ from traditional management consultancies?",
    a: "Traditional firms often deliver a report and leave. BLEZZINGS Growth Partners combines strategic diagnosis with frontline execution: we work alongside leadership, sales managers and channel partners in the market to implement playbooks, refine coverage and institutionalise the routines that keep results going.",
  },
  {
    q: "What industries does BLEZZINGS Growth Partners serve?",
    a: "We consult across eight core industries, including FMCG & consumer products, consumer durables & electronics, manufacturing & B2B, retail/D2C & e-commerce, real estate, education, media and professional services — in India and for Indian businesses expanding internationally.",
  },
  {
    q: "How does an engagement with BLEZZINGS Growth Partners start?",
    a: "Every engagement begins with a discovery conversation, followed by a diagnostic of your actual numbers and channels. Only then do we scope a proposal that defines the work, the deliverables and how progress will be judged — so you can evaluate the fit before committing to anything.",
  },
  {
    q: "Can BLEZZINGS Growth Partners help fix underperforming distributor and dealer networks?",
    a: "Yes. Channel and route-to-market transformation is one of our primary specialties. We work on territory and beat planning, distributor economics and margins, trade spend effectiveness, market coverage and partner performance management.",
  },
  {
    q: "Where is BLEZZINGS Growth Partners located?",
    a: "BLEZZINGS Growth Partners is headquartered in Whitefield, Bengaluru, Karnataka, India, and works with clients across India's major commercial hubs and beyond.",
  },
];

const TRUST_ANCHORS = [
  "4 core practices",
  "27 specialist services",
  "104 free growth tools",
  "Bengaluru, India",
];

export default function GrowthPartnersPage() {
  const [bookOpen, setBookOpen] = useState(false);
  const [joinOpen, setJoinOpen] = useState(false);

  usePageMeta({
    title: "Sales & Marketing Transformation Consulting | BLEZZINGS Growth Partners",
    description:
      "BLEZZINGS Growth Partners helps CEOs, founders and commercial leaders accelerate profitable growth through sales transformation, go-to-market strategy, channel economics and marketing effectiveness — strategy through execution, from Bengaluru, India.",
    canonical: "https://blezzings.co/businesses/growth-partners",
    image: "/photos/growth-partners.jpg",
    imageAlt: "Indian sales team reviewing commercial growth performance in a modern boardroom",
    breadcrumbs: [
      { name: "Home", url: "https://blezzings.co/" },
      { name: "Ecosystem", url: "https://blezzings.co/ecosystem" },
      { name: "BLEZZINGS Growth Partners", url: "https://blezzings.co/businesses/growth-partners" },
    ],
    faqs: FAQS.map((f) => ({ question: f.q, answer: f.a })),
  });

  const openBook = (source) => {
    track("book_consultation_open", { source: source || "growth-partners" });
    setBookOpen(true);
  };

  const insights = INSIGHT_SLUGS.map((s) => INSIGHTS.find((a) => a.slug === s)).filter(Boolean);

  return (
    <div data-testid="growth-partners-page" className="relative bg-white">
      <Nav onBook={() => openBook("nav-growth-partners")} />

      {/* 1 — Hero */}
      <section className="pt-32 md:pt-40 pb-16 md:pb-20 bg-[#F2F2F2] border-b border-black/10">
        <div className={`${GRID} grid grid-cols-12 gap-6 md:gap-10 items-center`}>
          <div className="col-span-12 lg:col-span-7">
            <SectionBadge label="BLEZZINGS Growth Partners" />
            <h1 className="font-serif-display font-bold text-4xl sm:text-5xl md:text-6xl mt-6 leading-[1.02] tracking-tight">
              Sales &amp; Marketing Transformation Consulting
            </h1>
            <p className="mt-6 font-serif-display italic text-2xl md:text-3xl text-black max-w-3xl leading-snug">
              Accelerate profitable growth through better Sales, Marketing and Go-to-Market execution.
            </p>
            <p className="mt-6 text-lg text-zinc-700 leading-relaxed max-w-2xl">
              We help CEOs, founders and commercial leaders fix sales productivity gaps,
              optimise channel economics and turn marketing spend into measurable,
              profitable growth — from strategy through frontline execution.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <button
                type="button"
                data-testid="gp-hero-cta"
                onClick={() => openBook("hero-growth-partners")}
                className="inline-flex items-center justify-center gap-2 h-12 px-6 bg-black text-white text-sm font-medium hover:bg-zinc-800 transition-colors focus:outline-none focus:ring-2 focus:ring-black focus:ring-offset-2"
              >
                Schedule a Consultation <ArrowUpRight className="h-4 w-4" />
              </button>
              <a
                href="#practices"
                data-testid="gp-hero-secondary"
                className="inline-flex items-center justify-center gap-2 h-12 px-6 border border-black text-black text-sm font-medium hover:bg-black hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-black focus:ring-offset-2"
              >
                Explore Services
              </a>
            </div>
            <div className="mt-8 flex flex-wrap gap-x-6 gap-y-2" data-testid="gp-trust-anchors">
              {TRUST_ANCHORS.map((t) => (
                <span key={t} className="text-xs font-semibold tracking-[0.14em] uppercase text-zinc-500">
                  {t}
                </span>
              ))}
            </div>
          </div>
          <div className="col-span-12 lg:col-span-5 relative mt-4 lg:mt-0">
            <div className="relative aspect-[4/5] w-full overflow-hidden bg-zinc-200 border border-zinc-300">
              <img
                data-testid="gp-hero-image"
                src="/photos/growth-partners.jpg"
                alt="Indian sales team reviewing commercial growth performance in a modern boardroom"
                className="absolute inset-0 w-full h-full object-cover slow-scale img-edit"
                loading="eager"
              />
              <div className="absolute inset-0 bg-black/10" />
            </div>
            <div className="hidden lg:block absolute -z-10 -right-6 -bottom-6 w-32 h-32 bg-black" aria-hidden />
          </div>
        </div>
      </section>

      {/* 2 — AEO definition + pillars */}
      <section className="py-16 md:py-24" data-testid="gp-at-a-glance">
        <div className={`${GRID} max-w-5xl`}>
          <SectionBadge label="At a Glance" />
          <p className="mt-6 text-lg md:text-xl text-zinc-800 leading-relaxed max-w-4xl">
            <strong className="font-semibold text-black">BLEZZINGS Growth Partners</strong> is a
            specialist Sales &amp; Marketing Transformation consulting firm based in Bengaluru, India.
            It works directly with CEOs, founders and commercial leaders to diagnose sales productivity
            bottlenecks, redesign go-to-market and distribution channel architectures, and execute
            commercial transformation strategies that drive profitable, scalable growth.
          </p>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { label: "Strategy", desc: "Grounding commercial direction in rigorous market and customer reality." },
              { label: "Execution", desc: "Working alongside frontline teams in the market to land real change." },
              { label: "Growth", desc: "Converting sales and channel momentum into bottom-line profitability." },
            ].map((p) => (
              <div key={p.label} className="border-t-2 border-black pt-5" data-testid={`gp-pillar-${p.label.toLowerCase()}`}>
                <h2 className="font-serif-display font-bold text-2xl text-black">{p.label}</h2>
                <p className="mt-2 text-[15px] text-zinc-600 leading-relaxed">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 3 — Four practices */}
      <section id="practices" className="py-16 md:py-24 bg-[#F2F2F2]" data-testid="gp-practices">
        <div className={GRID}>
          <SectionBadge label="Our Core Practices" />
          <h2 className="font-serif-display font-bold text-3xl md:text-4xl mt-6 leading-tight max-w-3xl">
            End-to-end commercial expertise across four disciplines.
          </h2>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {PRACTICES.map((p, i) => (
              <Link
                key={p.href}
                to={p.href}
                data-testid={`gp-practice-${i}`}
                className="group bg-white border border-black/10 p-7 hover:border-black transition-colors flex flex-col"
              >
                <div className="text-[11px] font-semibold tracking-[0.28em] text-zinc-400">{p.count.toUpperCase()}</div>
                <h3 className="font-serif-display font-bold text-xl mt-3 text-black leading-tight">{p.name}</h3>
                <p className="mt-3 text-sm text-zinc-600 leading-relaxed flex-1">{p.desc}</p>
                <span className="mt-5 inline-flex items-center gap-2 text-sm font-medium text-black">
                  Explore practice
                  <ArrowUpRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1 group-hover:-translate-y-1" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* 4 — Six ways we help (linked) */}
      <section className="py-16 md:py-24" data-testid="gp-services">
        <div className={GRID}>
          <SectionBadge label="What We Do" />
          <h2 className="font-serif-display font-bold text-3xl md:text-4xl mt-6 leading-tight max-w-3xl">
            Six high-impact commercial interventions.
          </h2>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {SERVICES.map((s, i) => (
              <Link
                key={s.href}
                to={s.href}
                data-testid={`gp-service-${i}`}
                className="group bg-white border border-black/10 p-7 md:p-8 hover:border-black transition-colors flex flex-col"
              >
                <div className="text-[11px] font-semibold tracking-[0.28em] text-zinc-400">{String(i + 1).padStart(2, "0")}</div>
                <h3 className="font-serif-display font-bold text-xl md:text-2xl mt-3 text-black leading-tight">{s.name}</h3>
                <p className="mt-3 text-[15px] text-zinc-600 leading-relaxed flex-1">{s.desc}</p>
                <span className="mt-5 inline-flex items-center gap-2 text-sm font-medium text-black">
                  View service
                  <ArrowUpRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1 group-hover:-translate-y-1" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* 5 — Image band */}
      <section className="pb-16 md:pb-24">
        <div className={`${GRID} relative`}>
          <div className="relative aspect-[16/10] md:aspect-[21/9] w-full overflow-hidden bg-zinc-200 border border-zinc-300">
            <img
              data-testid="gp-band-image"
              src="/photos/expertise-sales.jpg"
              alt="BLEZZINGS consultants working with a client's commercial team in a strategy session"
              className="absolute inset-0 w-full h-full object-cover slow-scale img-edit"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-black/10" />
          </div>
          <div className="hidden lg:block absolute -z-10 -left-6 -bottom-6 w-32 h-32 bg-black" aria-hidden />
        </div>
      </section>

      {/* 6 — Process */}
      <section className="py-16 md:py-24 bg-[#F2F2F2]" data-testid="gp-process">
        <div className={GRID}>
          <SectionBadge label="From Strategy to Execution" />
          <h2 className="font-serif-display font-bold text-3xl md:text-4xl mt-6 leading-tight max-w-3xl">
            Strategy creates direction. Execution creates results.
          </h2>
          <p className="mt-4 text-lg text-zinc-700 leading-relaxed max-w-3xl">
            Our engagements can span the full transformation journey — and each stage has a
            defined purpose and output.
          </p>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {PROCESS.map((s, i) => (
              <div key={s.name} className="bg-white border border-black/10 p-6" data-testid={`gp-process-${i}`}>
                <div className="flex items-center gap-3">
                  <span className="inline-flex items-center justify-center h-8 w-8 bg-black text-white text-xs font-semibold">{i + 1}</span>
                  <h3 className="text-[15px] font-semibold text-black tracking-wide">{s.name}</h3>
                </div>
                <p className="mt-3 text-sm text-zinc-600 leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
          <p className="mt-8 text-sm text-zinc-600">
            Want the detail?{" "}
            <Link to="/how-we-engage" data-testid="gp-process-engage-link" className="text-black font-medium underline underline-offset-4 decoration-zinc-300 hover:decoration-black transition-colors">
              See how engagements work →
            </Link>
          </p>
        </div>
      </section>

      {/* 7 — Audience */}
      <section className="py-16 md:py-24" data-testid="gp-audience">
        <div className={GRID}>
          <SectionBadge label="Who We Work With" />
          <h2 className="font-serif-display font-bold text-3xl md:text-4xl mt-6 leading-tight max-w-3xl">
            Built for ambitious leaders and growing organisations.
          </h2>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {AUDIENCE.map((g, i) => (
              <div key={g.name} className="border-t border-black pt-5" data-testid={`gp-audience-${i}`}>
                <div className="text-[11px] font-semibold tracking-[0.28em] text-zinc-500">{String(i + 1).padStart(2, "0")}</div>
                <h3 className="font-serif-display font-bold text-xl mt-2 text-black leading-tight">{g.name}</h3>
                <p className="mt-3 text-sm text-zinc-600 leading-relaxed">{g.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 8 — Tools & insights (intellectual capital) */}
      <section className="py-16 md:py-24 bg-[#F2F2F2]" data-testid="gp-capital">
        <div className={GRID}>
          <SectionBadge label="Proof & Intellectual Capital" />
          <h2 className="font-serif-display font-bold text-3xl md:text-4xl mt-6 leading-tight max-w-3xl">
            Try the thinking before you buy it.
          </h2>
          <p className="mt-4 text-lg text-zinc-700 leading-relaxed max-w-3xl">
            Our diagnostic tools and executive briefings are the same frameworks we use in
            client work — free to use, ungated.
          </p>
          <div className="mt-10 grid grid-cols-1 lg:grid-cols-12 gap-6">
            <Link
              to="/tools"
              data-testid="gp-tools-card"
              className="group lg:col-span-5 bg-black text-white p-8 flex flex-col justify-between min-h-[220px] hover:bg-zinc-900 transition-colors"
            >
              <div>
                <div className="text-[11px] font-semibold tracking-[0.28em] text-zinc-400">104 FREE INTERACTIVE TOOLS</div>
                <h3 className="font-serif-display font-bold text-2xl md:text-3xl mt-3 leading-tight">
                  The Growth Tools library
                </h3>
                <p className="mt-3 text-sm text-zinc-300 leading-relaxed">
                  Pricing leakage, break-even ROAS, sales productivity, distribution planning —
                  diagnose your own numbers with the instruments we built for client work.
                </p>
              </div>
              <span className="mt-6 inline-flex items-center gap-2 text-sm font-medium">
                Explore all tools
                <ArrowUpRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1 group-hover:-translate-y-1" />
              </span>
            </Link>
            <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-4">
              {insights.map((a) => (
                <Link
                  key={a.slug}
                  to={`/insights/${a.slug}`}
                  data-testid={`gp-insight-${a.slug}`}
                  className="group bg-white border border-black/10 p-6 hover:border-black transition-colors flex flex-col"
                >
                  <div className="text-[11px] font-semibold tracking-[0.28em] text-zinc-400">
                    INSIGHT · {readTimeLabel(a).toUpperCase()}
                  </div>
                  <h3 className="font-serif-display font-bold text-lg mt-3 text-black leading-snug flex-1">{a.title}</h3>
                  <span className="mt-4 inline-flex items-center gap-2 text-sm font-medium text-black">
                    Read
                    <ArrowUpRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1 group-hover:-translate-y-1" />
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* 9 — Founder note */}
      <section className="py-16 md:py-24" data-testid="gp-founder">
        <div className={`${GRID} grid grid-cols-12 gap-8 md:gap-12 items-center max-w-5xl`}>
          <div className="col-span-12 md:col-span-4 relative">
            <div className="relative aspect-[4/5] w-full overflow-hidden bg-zinc-200 border border-zinc-300">
              <img
                src="/photos/about.jpg"
                alt="Khalid Kamal Rumi, Founder & Managing Partner, BLEZZINGS"
                className="absolute inset-0 w-full h-full object-cover slow-scale img-edit"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-black/10" />
            </div>
            <div className="hidden md:block absolute -z-10 -left-6 -bottom-6 w-32 h-32 bg-black" aria-hidden />
          </div>
          <div className="col-span-12 md:col-span-8">
            <SectionBadge label="Leadership & Credibility" />
            <h2 className="font-serif-display font-bold text-3xl md:text-4xl mt-6 leading-tight">
              Senior operating experience, not theoretical advice.
            </h2>
            <p className="mt-6 font-serif-display italic text-2xl text-black leading-snug max-w-2xl">
              "Commercial transformation succeeds when strategy meets frontline execution.
              We don't just leave a slide deck — we work with your team to solve the problem
              in the market."
            </p>
            <p className="mt-6 text-[15px] text-zinc-700 leading-relaxed max-w-2xl">
              <strong className="font-semibold text-black">Khalid Kamal Rumi</strong>, Founder &amp; Managing
              Partner, led senior operating, sales and marketing roles across major organisations
              before founding BLEZZINGS.{" "}
              <Link to="/leadership" data-testid="gp-leadership-link" className="text-black font-medium underline underline-offset-4 decoration-zinc-300 hover:decoration-black transition-colors">
                Read the leadership story →
              </Link>
            </p>
          </div>
        </div>
      </section>

      {/* 10 — Philosophy band */}
      <section className="py-16 md:py-24 bg-black text-white" data-testid="gp-philosophy">
        <div className="max-w-4xl mx-auto px-6 sm:px-8 md:px-12">
          <SectionBadge label="Our Philosophy" tone="light" />
          <p className="mt-6 font-serif-display text-2xl md:text-3xl leading-snug">
            Growth should not simply increase the topline. The objective is profitable,
            sustainable growth — improving not only how much a business sells, but how
            effectively it acquires customers, deploys resources, manages channels and
            converts growth into economic value.
          </p>
        </div>
      </section>

      {/* 11 — FAQ */}
      <section className="py-16 md:py-24" data-testid="gp-faq">
        <div className="max-w-3xl mx-auto px-6 sm:px-8 md:px-12">
          <SectionBadge label="Frequently Asked Questions" />
          <h2 className="font-serif-display font-bold text-3xl md:text-4xl mt-6 leading-tight">
            Answers, plainly.
          </h2>
          <Accordion type="single" collapsible className="mt-10">
            {FAQS.map((f, i) => (
              <AccordionItem key={i} value={`faq-${i}`} className="border-black/10" data-testid={`gp-faq-${i}`}>
                <AccordionTrigger className="text-left text-[15px] font-semibold text-black hover:no-underline">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-[15px] text-zinc-600 leading-relaxed">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* 12 — Closing CTA */}
      <section className="py-20 md:py-28 bg-[#F2F2F2] text-center" data-testid="gp-closing">
        <div className="max-w-3xl mx-auto px-6 sm:px-8 md:px-12">
          <h2 className="font-serif-display font-bold text-3xl md:text-4xl leading-tight">
            Have a Sales or Marketing challenge that is holding back growth?
          </h2>
          <p className="mt-4 font-serif-display italic text-xl md:text-2xl text-zinc-700">
            Let's solve it — and execute the solution.
          </p>
          <button
            type="button"
            onClick={() => openBook("closing-growth-partners")}
            data-testid="gp-closing-cta"
            className="mt-8 inline-flex items-center justify-center gap-2 h-12 px-6 bg-black text-white text-sm font-medium hover:bg-zinc-800 transition-colors focus:outline-none focus:ring-2 focus:ring-black focus:ring-offset-2"
          >
            Talk to Growth Partners <ArrowUpRight className="h-4 w-4" />
          </button>
          <p className="mt-6 text-sm text-zinc-600">
            <Link to="/how-we-engage" data-testid="gp-closing-engage-link" className="underline underline-offset-4 decoration-zinc-300 hover:decoration-black hover:text-black transition-colors">
              Explore how engagements work
            </Link>
          </p>
          <p className="mt-10 font-serif-display italic text-lg text-zinc-600">Strategy. Execution. Growth.</p>
        </div>
      </section>

      <Footer onJoin={() => setJoinOpen(true)} />
      <FloatingActions onBook={() => openBook("floating-growth-partners")} />
      <BookConsultationDialog open={bookOpen} onOpenChange={setBookOpen} />
      <JoinUsDialog open={joinOpen} onOpenChange={setJoinOpen} />
    </div>
  );
}
